import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import Card from '../../components/UI/Card';

const TermsOfServicePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="p-4 flex items-center gap-3">
        <button
          onClick={() => navigate('/settings/privacy-security')}
          className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-white">Terms of Service</h1>
      </div>

      <div className="px-4 pb-8">
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <FileText size={32} className="text-blue-600 dark:text-blue-400" />
            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">πPi Dating Terms of Service</h2>
              <p className="text-gray-600 dark:text-gray-300">Last updated: {new Date().toLocaleDateString()}</p>
            </div>
          </div>

          <div className="space-y-6 text-gray-700 dark:text-gray-300">
            <section>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">1. Acceptance of Terms</h3>
              <p>By accessing and using πPi Dating ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.</p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">2. Eligibility</h3>
              <div className="space-y-2">
                <p>To use πPi Dating, you must:</p>
                <p>• Be at least 18 years old</p>
                <p>• Have a valid Pi Network account</p>
                <p>• Provide accurate and truthful information</p>
                <p>• Not be prohibited from using the service under applicable law</p>
                <p>• Not have been previously banned from the platform</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">3. Account Responsibilities</h3>
              <div className="space-y-2">
                <p><strong>Profile Accuracy:</strong> You must provide accurate, current, and complete information about yourself.</p>
                <p><strong>Security:</strong> You are responsible for maintaining the confidentiality of your account credentials.</p>
                <p><strong>Authorized Use:</strong> You may not allow others to use your account or create multiple accounts.</p>
                <p><strong>Verification:</strong> We may verify your identity through Pi Network or other means.</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">4. Prohibited Conduct</h3>
              <div className="space-y-2">
                <p>You agree not to:</p>
                <p>• Harass, abuse, or harm other users</p>
                <p>• Post false, misleading, or inappropriate content</p>
                <p>• Use the service for commercial purposes without permission</p>
                <p>• Attempt to access other users' accounts or private information</p>
                <p>• Distribute spam, viruses, or malicious content</p>
                <p>• Violate any applicable laws or regulations</p>
                <p>• Impersonate another person or entity</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">5. Pi Network Integration</h3>
              <div className="space-y-2">
                <p><strong>Payments:</strong> Premium features are paid using Pi cryptocurrency through the Pi Network.</p>
                <p><strong>Wallet Connection:</strong> You authorize πPi Dating to interact with your Pi wallet for transactions.</p>
                <p><strong>Transaction Fees:</strong> All Pi Network transaction fees apply to payments made through the app.</p>
                <p><strong>Refunds:</strong> Pi payments are generally non-refundable except as required by law.</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">6. Premium Features</h3>
              <div className="space-y-2">
                <p><strong>Subscription:</strong> Premium features require a paid subscription using Pi cryptocurrency.</p>
                <p><strong>Auto-Renewal:</strong> Subscriptions automatically renew unless cancelled before the renewal date.</p>
                <p><strong>Cancellation:</strong> You can cancel your subscription at any time through your account settings.</p>
                <p><strong>Feature Access:</strong> Premium features are available only during active subscription periods.</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">7. Content and Intellectual Property</h3>
              <div className="space-y-2">
                <p><strong>Your Content:</strong> You retain ownership of content you post but grant us a license to use it for service operation.</p>
                <p><strong>Our Content:</strong> All app content, design, and functionality are owned by πPi Dating and protected by copyright.</p>
                <p><strong>User-Generated Content:</strong> You are responsible for content you post and must have rights to share it.</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">8. Privacy and Data</h3>
              <p>Your privacy is important to us. Please review our Privacy Policy to understand how we collect, use, and protect your information. By using the service, you consent to our data practices as described in the Privacy Policy.</p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">9. Safety and Reporting</h3>
              <div className="space-y-2">
                <p><strong>Safety First:</strong> Always prioritize your safety when meeting people from the app.</p>
                <p><strong>Reporting:</strong> Report any inappropriate behavior, harassment, or safety concerns immediately.</p>
                <p><strong>Investigation:</strong> We investigate all reports and may take action including account suspension or termination.</p>
                <p><strong>Cooperation:</strong> We may cooperate with law enforcement when necessary for user safety.</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">10. Disclaimers and Limitations</h3>
              <div className="space-y-2">
                <p><strong>Service Availability:</strong> We do not guarantee uninterrupted or error-free service.</p>
                <p><strong>User Interactions:</strong> We are not responsible for interactions between users outside the app.</p>
                <p><strong>Third-Party Services:</strong> We are not liable for issues with Pi Network or other third-party services.</p>
                <p><strong>Matching:</strong> We do not guarantee matches or successful relationships.</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">11. Termination</h3>
              <div className="space-y-2">
                <p><strong>By You:</strong> You may delete your account at any time through app settings.</p>
                <p><strong>By Us:</strong> We may suspend or terminate accounts that violate these terms.</p>
                <p><strong>Effect:</strong> Upon termination, your access to the service ends and your data may be deleted.</p>
                <p><strong>Survival:</strong> Certain provisions of these terms survive termination.</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">12. Changes to Terms</h3>
              <p>We may modify these terms at any time. We will notify users of material changes through the app or email. Continued use after changes constitutes acceptance of the new terms.</p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">13. Contact Information</h3>
              <div className="space-y-2">
                <p>For questions about these terms, please contact us:</p>
                <p>• Email: legal@pidating.app</p>
                <p>• Support: help@pidating.app</p>
                <p>• Address: πPi Dating Legal Team, Pioneer Square, Digital City</p>
              </div>
            </section>

            <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Fair and Transparent:</strong> These terms are designed to create a safe, respectful environment for all πPi Dating users. We're committed to fair treatment and transparent policies that protect both users and the platform.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfServicePage;